import { useState, useMemo } from "react";
import { DropdownButton } from "../atoms/DropdownButton";
import { SearchInput } from "../atoms/SearchInput";
import { StatsGrid } from "../organisms/StatsGrid";
import { DataTable } from "../organisms/DataTable";
import { PageHeader } from "../molecules/PageHeader";
import { FilterButton } from "../atoms/FilterButton";
import { More } from "iconsax-react";

import {
  FINANCE_STATS,
  recentPayouts,
  MOCK_TRANSACTIONS,
  TRANSACTION_TABLE_HEADERS,
  type Transaction,
} from "@/data/financenpaymentsdata";

const TransactionTableRow = ({ transaction }: { transaction: Transaction }) => (
  <tr className="border-b border-dark-30 hover:bg-dark-30 transition-colors">
    <td className="px-6 py-4 text-dark-text font-medium">
      {transaction.transactionID}
    </td>
    <td className="px-6 py-4 text-dark-text">{transaction.userName}</td>
    <td className="px-6 py-4 text-dark-text">{transaction.type}</td>
    <td className="px-6 py-4 text-dark-text">{transaction.amount}</td>
    <td className="px-6 py-4 text-dark-text">{transaction.paymentMethod}</td>
    <td className="px-6 py-4 text-dark-text">{transaction.date}</td>
    <td className="px-6 py-4 text-dark-text">{transaction.status}</td>
    <td className="px-6 py-4">
      <button className="text-dark-100 hover:text-dark-text transition-colors p-1">
        <More className="w-6 h-6" color="#EEEEEE" />
      </button>
    </td>
  </tr>
);

const PayoutsOverview = () => {
  const pendingAmount = 3200;
  const pendingRequests = 45;
  const completedAmount = 18500;

  return (
    <div className="bg-dark-10 rounded-[5px] p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-dark-text">
          Payouts Overview
        </h2>
        <DropdownButton label="Last 30 Days" />
      </div>

      {/* Stats */}
      <div className="space-y-4 mb-6">
        <div className="flex items-center justify-between">
          <span className="text-dark-80">Pending Payouts:</span>
          <span className="font-semibold text-dark-text">
            ${pendingAmount.toLocaleString()}
            <span className="text-dark-text ml-1">
              ({pendingRequests} requests)
            </span>
          </span>
        </div>

        <div className="flex items-center justify-between">
          <span className=" text-dark-80">Completed Payouts:</span>
          <span className="font-semibold text-dark-text">
            ${completedAmount.toLocaleString()}
          </span>
        </div>
      </div>

      <div className="text-dark-text">
        <h3 className="text-base font-semibold mb-4">Recent Payouts</h3>

        <div className="space-y-4">
          {recentPayouts.map((payout, index) => (
            <div key={payout.id} className="flex items-center gap-4">
              {/* Number */}
              <span className="text-sm w-4 font-medium text-dark-text">
                {index + 1}.
              </span>

              {/* Details */}
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium">{payout.name}</span>
                  <span className=" bg-[#222222] px-3 rounded">
                    ${payout.amount.toLocaleString()}
                  </span>
                </div>
                <p className="text-sm text-gray-500">{payout.method}</p>
              </div>

              {/* Status Badge */}
              <span className="px-3 py-1 bg-green-500/10 text-green-400 text-xs font-medium rounded">
                {payout.status}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const FinanceChart = () => {
  return (
    /* Revenue Chart Placeholder */
    <div className="bg-dark-10 rounded-[5px] p-6">
      <DropdownButton label="Last 30 Days" />

      {/* Donut Chart Placeholder */}
      <div className="flex items-center justify-center h-[250px] mt-6">
        <div className="relative w-[200px] h-[200px]">
          <svg viewBox="0 0 100 100" className="transform -rotate-90">
            <circle
              cx="50"
              cy="50"
              r="40"
              fill="none"
              stroke="#E3F7D3"
              strokeWidth="20"
              strokeDasharray="90 314"
            />
            <circle
              cx="50"
              cy="50"
              r="40"
              fill="none"
              stroke="#0090FF"
              strokeWidth="20"
              strokeDasharray="90 314"
              strokeDashoffset="-90"
            />
            <circle
              cx="50"
              cy="50"
              r="40"
              fill="none"
              stroke="#30A46C"
              strokeWidth="20"
              strokeDasharray="250 314"
              strokeDashoffset="-170"
            />
          </svg>
        </div>
      </div>

      <div className="flex items-center justify-center gap-6 mt-4">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 bg-[#0090FF] rounded-sm" />
          <span className="text-sm text-dark-100">Paid Groups</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 bg-[#30A46C] rounded-sm" />
          <span className="text-sm text-dark-100">Subscriptions</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 bg-[#E3F7D3] rounded-sm" />
          <span className="text-sm text-dark-100">Ads/Other</span>
        </div>
      </div>
    </div>
  );
};

const Finance = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  // Filter users based on search term
  const filteredTransactions = useMemo(() => {
    if (!searchTerm) return MOCK_TRANSACTIONS;
    return MOCK_TRANSACTIONS.filter((transaction) =>
      transaction.id.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [searchTerm]);

  return (
    <div className="max-w-7xl mx-auto space-y-10">
      <PageHeader title="Finance & Payments" />

      {/* Stats Grid */}
      <StatsGrid stats={FINANCE_STATS} columns={3} />

      {/* Payouts Overview */}
      <div className="mt-10">
        <h1 className="text-2xl font-medium text-dark-text mb-4.5">
          Revenue Summary
        </h1>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <FinanceChart />
          <PayoutsOverview />
        </div>
      </div>

      {/* User Table Section */}
      <DataTable
        title="Recent Transactions"
        headers={TRANSACTION_TABLE_HEADERS}
        data={filteredTransactions}
        renderRow={(transaction) => (
          <TransactionTableRow key={transaction.id} transaction={transaction} />
        )}
        filters={
          <>
            <FilterButton label="Type" />
            <FilterButton label="Status" />
            <FilterButton label="Payment Method" />
            <SearchInput value={searchTerm} onChange={setSearchTerm} />
          </>
        }
        currentPage={currentPage}
        onPageChange={setCurrentPage}
      />
    </div>
  );
};

export default Finance;
